import { Provider, connect } from "react-redux";
import { createStore, applyMiddleware } from "redux";
import { thunk } from "redux-thunk";
import { composeWithDevTools } from "@redux-devtools/extension";
import { Fragment } from "react";

import rootReducer from "@js/reducers/mui/campaigns/show.js";

import BaseComponent from "@js/components/base_component.jsx";
import { TabController, TabPane } from "@js/components/widgets/tabs.jsx";
import { messageTextShow, messageTextClose } from "@js/actions/mui/campaigns/show.js";
import LoadingSpinner from "@js/components/studio/utilities/loading_spinner.jsx";
import classNames from "@js/helpers/class_names.js";

class MuiCampaignsShow extends BaseComponent {

  constructor(props) {
    super(props);

    this.store = createStore(
      rootReducer,
      {
        campaign: props.campaign,
        show_customer_identifier: props.can_view_customer_identifiers,
      },
      composeWithDevTools(applyMiddleware(thunk)),
    );
  }

  render() {
    const campaign = this.props.campaign;

    return (
      <Provider store={ this.store }>
        <MuiCampaignsShowInner />
      </Provider>
    );
  }

}

const MuiCampaignsShowInner = connect((state) => ({
  campaign: state.campaign,
  show_customer_identifier: state.show_customer_identifier,
}))((props) => {
  const campaign = props.campaign;

  return (
    <div>
      <dl className="dl-horizontal">

        <dt>{ I18n.t("monitor.subject") }:</dt>
        <dd>{ campaign.subject }</dd>

        <dt>{ I18n.t("monitor.from") }:</dt>
        <dd>{ campaign.from_address }</dd>

        {
          props.show_customer_identifier &&
            <Fragment>
              <dt>{ I18n.t("headings.ccustid") }:</dt>
              <dd>{ campaign.ccustid }</dd>
            </Fragment>
        }

        <dt>{ I18n.t("monitor.campaign_id") }:</dt>
        <dd>{ campaign.ccampid }</dd>

        <dt>{ I18n.t("monitor.date") }:</dt>
        <dd>{ campaign.date_short }</dd>

      </dl>

      <TabController>

        <TabPane title={ I18n.t("monitor.overview") }>
          <OverviewStats campaign={ campaign } />
          {/*
            See the file help-studio/development/monitor_isp_groups.markdown
            for locations that need to be updated with new ISP groups.
          */}
          <IspStats campaign={ campaign } isp="global" />
          <IspStats campaign={ campaign } isp="us" />
          <IspStats campaign={ campaign } isp="canada" />
          <IspStats campaign={ campaign } isp="europe" />
          <IspStats campaign={ campaign } isp="asia-pacific" />
          <IspStats campaign={ campaign } isp="b2b" />
          <IspStats campaign={ campaign } isp="reference" />
        </TabPane>

        <TabPane title={ I18n.t("monitor.message_details") }>
          <OverviewStats campaign={ campaign } />
          <PerRecipientStats campaign={ campaign } isReferenceTable={ false } />
          <PerRecipientStats campaign={ campaign } isReferenceTable={ true } />
        </TabPane>

      </TabController>

      <ShowMessageDetailsModal />
    </div>
  );
});

const ShowMessageDetailsModal = connect((state) => ({
  messageId: state.showMessageId,
  loading:   state.showMessageLoading,
  text:      state.showMessageText,
  error:     state.showMessageError,
}))((props) => {
  if ( !props.messageId ) return null;

  return (
    <div className="modal show">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <button type="button" className="close" onClick={ () => props.dispatch(messageTextClose()) }>
              <span aria-hidden="true">&times;</span>
            </button>
            <h4 className="text-left modal-title">{ I18n.t("monitor.message_details") }</h4>
          </div>
          <div
            className="text-left modal-body clearfix"
            style={{ minHeight: "175px", maxHeight: "500px", overflow: "scroll", whiteSpace: "pre", fontFamily: "monospace", paddingTop: "0px" }}
          >
            {
              props.loading ? (
                <LoadingSpinner width="32" height="32" />
              ) : props.error ? (
                <span className="text-danger">{ props.error }</span>
              ) : (
                props.text
              )
            }
          </div>
        </div>
      </div>
    </div>
  );
});

const OverviewStats = (props) => (
  <div>
    <h3>Overview Stats</h3>

    <table className="table table-hover">
      <thead>
        <tr>
          <th className="text-right" style={{width: "125px"}}>{ I18n.t("monitor.inbox") }</th>
          <th className="text-right" style={{width: "125px"}}>{ I18n.t("monitor.bulk") }</th>
          <th className="text-right" style={{width: "125px"}}>{ I18n.t("monitor.missing") }</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="text-right">{ percent(props.campaign.pct_inbox) + "%" }</td>
          <td className="text-right">{ percent(props.campaign.pct_bulk) + "%" }</td>
          <td className="text-right">{ percent(props.campaign.pct_missing) + "%" }</td>
          <td><CampaignProgressGraph campaign={ props.campaign } /></td>
        </tr>
      </tbody>
    </table>
  </div>
);

const CampaignProgressGraph = (props) => {
  let campaign = props.campaign;
  let inbox    = campaign.pct_inbox ? parseFloat(campaign.pct_inbox).toFixed(0) : 0;
  let bulk     = campaign.pct_bulk  ? parseFloat(campaign.pct_bulk).toFixed(0)  : 0;
  let missing  = 100 - inbox - bulk;

  return (
    <div className="progress" style={{margin: "0px 0px 0px 25px"}}>
      <div className="progress-bar progress-bar-primary" style={{width: inbox+"%"}} />
      <div className="progress-bar progress-bar-warning" style={{width: bulk+"%"}} />
      <div className="progress-bar progress-bar-danger" style={{width: missing+"%"}} />
    </div>
  );
};

const IspStats = (props) => {
  const campaign = props.campaign;
  const ispKey   = props.isp;

  return campaign.isp_stats[ispKey] ? (
    <div>
      <h3>ISP Statistics for { I18n.t(`isp_zone.${ ispKey }`) }</h3>

      <table className="table table-hover">
        <thead>
          <tr>
            <th></th>
            <th className="text-right" style={{width: "200px"}}>{ I18n.t("monitor.domain") }</th>
            <th className="text-right" style={{width: "90px"}}>% { I18n.t("monitor.inbox") }</th>
            <th className="text-right" style={{width: "90px"}}>% { I18n.t("monitor.bulk") }</th>
            <th className="text-right" style={{width: "90px"}}>% { I18n.t("monitor.missing") }</th>
            <th className="text-right" style={{width: "90px"}}>{ I18n.t("monitor.dups") }</th>
          </tr>
        </thead>
        <tbody>
          {
            campaign.isp_stats[ispKey].map(
              (row, ii) => <IspStatsRow row={ row } key={ ii } />
            )
          }
        </tbody>
      </table>
    </div>
  ) : (
    null
  );
};

const IspStatsRow = (props) => (
  <tr>
    <td>{ props.row.name }</td>
    <td className="text-right">{ props.row.domain }</td>
    <td className="text-right">{ percent(props.row.pct_inbox) }</td>
    <td className={classNames({"text-right": true, "text-danger": props.row.pct_bulk>0})}>{ percent(props.row.pct_bulk) }</td>
    <td className={classNames({"text-right": true, "text-danger": props.row.pct_missing>0})}>{ percent(props.row.pct_missing) }</td>
    <td className="text-right">{ props.row.count_duplicate }</td>
  </tr>
);

const PerRecipientStats = (props) => {
  const campaign         = props.campaign;
  const isReferenceTable = !!props.isReferenceTable;
  const seedStats        = campaign.seed_stats.filter(row => isReferenceTable == !!row.name.match(/Reference/));

  return (
    <div>
      <h3>{ isReferenceTable ? I18n.t("monitor.stats_for_reference_accounts") : I18n.t("monitor.stats_per_recipient") }</h3>

      <table className="table table-hover">
        <thead>
          <tr>
            <th>{ I18n.t("monitor.isp") }</th>
            <th>{ I18n.t("monitor.domain") }</th>
            <th>{ I18n.t("monitor.username") }</th>
            <th style={{width: "160px"}}>{ I18n.t("monitor.time_collected") }</th>
            <th>{ I18n.t("monitor.from") }</th>
            <th style={{width: "80px"}}>{ I18n.t("monitor.location") }</th>
            <th style={{width: "80px"}}>{ I18n.t("monitor.duplicate") }</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          {
            seedStats.map(
              (row, ii) => <PerRecipientStatsRow row={ row } key={ ii } />
            )
          }
        </tbody>
      </table>
    </div>
  );
};

const PerRecipientStatsRow = connect((state) => ({
}))((props) => {
  const row           = props.row;
  const allowDownload = row.allow_download;
  const wasCollected  = !!row.time_collected;
  const location      = row.location == 0 ? I18n.t("monitor.inbox") : row.location == 1 ? I18n.t("monitor.bulk") : I18n.t("monitor.missing");
  const duplicate     = ( row.duplicate && row.duplicate > 0 ) ? row.duplicate : "";

  return (
    <tr>
      <td>{ row.name }</td>
      <td>{ row.domain }</td>
      <td>{ row.username }</td>
      <td>{ row.time_collected_short }</td>
      <td>{ row.from_address }</td>
      <td>{ location }</td>
      <td>{ duplicate }</td>
      <td className="text-right">
        {
          wasCollected && allowDownload && (
            <div>
              <ButtonWithIcon
                href={ Routes.mui_message_download_path(row.message_id) }
                icon="download"
                size="small"
                type="index"
                download={ true }
                // Workaround for issue with Turbolinks showing the progress
                // bar forever when clicking this link.
                onClick={ () => setTimeout(() => Turbolinks.enableProgressBar().done(), 100) }
              />
              <ButtonWithIcon
                icon="search"
                size="small"
                type="index"
                onClick={ () => props.dispatch(messageTextShow(row.message_id)) }
              />
            </div>
          )
        }
      </td>
    </tr>
  );
});

const percent = (value) => {
  if ( value === null ) {
    return null;
  }

  return parseFloat(value, 10).toFixed(1);
};

export default MuiCampaignsShow;
